import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';

import { Store } from '@ngrx/store';

import { Observable, Subscription } from 'rxjs';

import { AppState } from '../../../../state';
import { Study } from '../../../../models/study';
import {
  selectIsLoadingStudies,
  selectSelectedLanguage,
  selectStudies,
  selectStudiesLoaded
} from '../../../state/study/study.selectors';
import { StudyType } from '../../../../models/study-type';
import {setStudyStatus, STUDY_STATUS_MESSAGES} from '../../helpers/status-helper';
import { Role } from '../../../../models/role';
import { User } from '../../../../models/user';
import { selectUser, selectUserStudyTypes } from '../../../../state/user/user.selectors';
import { ReleasesDialogComponent } from '../../components/releases-dialog/releases-dialog.component';
import { selectLatestRelease } from '../../../state/release-note/release-notes.selector';
import { CreateStudyDialogComponent } from '../../components/create-study-dialog/create-study-dialog.component';
import { DASHBOARD_ROUTES } from '../../../dashboard.routes';
import { deleteStudy, duplicateStudy } from '../../../state/study/study.actions';
import { STUDIES_ROUTES } from '../../studies.routes';
import { Language } from 'src/app/models/language';
import { openDashboardConfirmationDialog } from '../../../state/dashboard.actions';
import { sortByDate } from '../../helpers/date-helper';
import {IPageInfo} from "ngx-virtual-scroller";
import {transformStudyFromApi} from "../../../state/study/study.effects";
import {StudyService} from "../../../services/study/study.service";
import {DashboardCamelCasePipe} from "../../../../core/pipes/dashboard-camel-case/dashboard-camel-case.pipe";


interface SortOption {
  value: string,
  viewValue: string,
}

@Component({
  selector: 'dashboard-studies',
  templateUrl: './studies.component.html',
  styleUrls: ['./studies.component.scss']
})
export class StudiesComponent implements OnInit, OnDestroy {
  studyTitleLengthLimit = 40;
  studyDescriptionLengthLimit = 80;
  StudyType = StudyType;
  Role = Role;

  loadingStudies$: Observable<boolean>;
  studiesLoaded$: Observable<boolean>;
  latestRelease$: Observable<string>;

  allStudies: Study[] = [];
  sortedStudies: Study[] = [];
  user: User;
  userStudyTypes: string[] = [];
  selectedLanguage: Language;
  page = 1;
  studiesPerPage = 50; // This is set in the API
  totalPages = 2;

  private subscriptions = new Subscription();
  private timer = null;

  sortOptions: SortOption[] = [
    { value: 'showAll', viewValue: 'All studies' },
    { value: 'showActive', viewValue: 'Active studies' },
    { value: 'showNonActive', viewValue: 'Non-active studies' },
    { value: 'latest', viewValue: 'Latest created' },
    { value: 'tiim', viewValue: 'TIIM Studies' },
    { value: 'cc', viewValue: 'Career Compass Studies' },
  ];

  searchStudies: string;
  selectedOption: string;

  constructor(public store: Store<AppState>,
              private dialog: MatDialog,
              private router: Router,
              private studyService: StudyService,
              private dashboardCamelCasePipe: DashboardCamelCasePipe) {
  }

  ngOnInit(): void {
    this.loadingStudies$ = this.store.select(selectIsLoadingStudies);
    this.studiesLoaded$ = this.store.select(selectStudiesLoaded);

    this.latestRelease$ = this.store.select(selectLatestRelease);

    this.subscriptions.add(this.store.select(selectStudies).subscribe((studies) => {
      this.allStudies = studies;
      this.sortedStudies = studies;
    }));

    this.subscriptions.add(this.store.select(selectUser).subscribe((user) => (this.user = user)));

    this.subscriptions.add(
      this.store.select(selectUserStudyTypes).subscribe((userStudyTypes) => (this.userStudyTypes = userStudyTypes))
    );

    this.subscriptions.add(
      this.store.select(selectSelectedLanguage).subscribe((language) => (this.selectedLanguage = language))
    );
  }

  checkActiveStudies(): Study[] {
    const activeStudies: Study[] = [];
    for (const study of this.allStudies) {
      if (study.status.startsWith(STUDY_STATUS_MESSAGES.RUNNING)) {
        activeStudies.push(study);
      }
    }
    return activeStudies;
  }

  getStudies(): Study[] {
    return this.sortedStudies;
  }

  showActiveStudies() {
    this.sortedStudies = this.checkActiveStudies();
    this.sortedStudies.sort((study1, study2) => sortByDate(study1?.createdAt, study2?.createdAt));
  }

  showAllStudies(): void {
    this.sortByDateCreated();
  }

  openCreateStudyDialog(): void {
    this.dialog.open(CreateStudyDialogComponent, {
      data: { userStudyTypes: this.userStudyTypes }
    });
  }

  openReleasesDialog(): void {
    this.dialog.open(ReleasesDialogComponent);
  }

  openStudyDetails(studyId: number): void {
    this.router.navigate([`${DASHBOARD_ROUTES.STUDIES}/${studyId}/${STUDIES_ROUTES.HOME}`]);
  }

  openDeleteStudyDialog(study: Study): void {
    this.store.dispatch(
      openDashboardConfirmationDialog({
        dialogData: {
          title: `Are you sure you want to delete this study?`,
          onConfirm: deleteStudy({ study })
        }
      })
    );
  }

  duplicateStudy(study: Study): void {
    this.store.dispatch(duplicateStudy({ study }));
  }

  getLanguageCodeToDisplay(study): string {
    return study?.languages?.includes(this.selectedLanguage?.code) ? this.selectedLanguage?.code : study?.languages[0];
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  sort(value: string) {
    this.selectedOption = value;
    switch (value) {
      case 'showAll':
        this.showAllStudies();
        break;
      case 'showActive':
        this.showActiveStudies();
        break;
      case 'showNonActive':
        this.showNonActiveStudies();
        break;
      case 'latest':
        this.sortByDateCreated();
        break;
      case 'tiim':
        this.sortByTIIMStudies();
        break;
      case 'cc':
        this.sortByCCStudies();
        break;
    }

    if (this.selectedOption !== '' && this.searchStudies === undefined) {
      this.filterStudies();
      this.searchStudies = '';
    }
  }

  showNonActiveStudies(): void {
    this.sortedStudies = this.allStudies;
    this.sortedStudies = this.sortedStudies.filter(study =>
      study.status.startsWith(STUDY_STATUS_MESSAGES.FINISHED)
    );
  }

  sortByDateCreated(): void {
    this.sortedStudies = this.allStudies;
    this.sortedStudies.sort((study1, study2) => sortByDate(study1?.createdAt, study2?.createdAt));
  }

  sortByTIIMStudies(): void {
    this.sortedStudies = this.allStudies;
    this.sortedStudies = this.sortedStudies.filter(study => study.type.includes(StudyType.TIIM));
  }

  sortByCCStudies(): void {
    this.sortedStudies = this.allStudies;
    this.sortedStudies = this.sortedStudies.filter(study => study.type.includes(StudyType.CC));
  }

  filterStudies(): void {
    if (this.searchStudies !== '' || (this.searchStudies === '' &&
      (this.selectedOption === 'cc' || this.selectedOption === 'tiim' || this.selectedOption === 'ss'))) {
      if (this.timer) {
        window.clearTimeout(this.timer);
      }
      this.timer = window.setTimeout(() => {
        this.timer = null;
        this.studyService.searchStudy(this.searchStudies, this.selectedOption).subscribe((studies) => {
          const transformedStudies = [];
          for (const study of studies) {
            const transformedStudy: Study = this.dashboardCamelCasePipe.keysToCamelCase(study);
            if (!transformedStudy.pivot || (transformedStudy.pivot && !transformedStudy.pivot.blocked)) {
              transformedStudy.status = setStudyStatus(transformedStudy.startsAt, transformedStudy.endsAt);
              transformedStudies.push(transformStudyFromApi(transformedStudy));
            }
          }
          this.sortedStudies = transformedStudies;
          this.allStudies = transformedStudies;
          if (this.selectedOption && this.selectedOption !== 'showAll') {
            this.sort(this.selectedOption);
          }
        });
      }, 300);
    } else if (this.selectedOption && this.selectedOption !== 'showAll') {
      this.sort(this.selectedOption);
    } else {
      this.getStudiesForPage(1).then(studies => {
        this.sortedStudies = studies;
        this.allStudies = studies;
      });
    }
  }

  fetchMore(event: IPageInfo) {
    if (event.endIndex < (this.studiesPerPage - 10) ||
      this.totalPages === Math.ceil(this.sortedStudies.length / this.studiesPerPage) ||
      event.endIndex !== this.sortedStudies.length - 1)
    {
      return;
    }
    this.fetchNextChunk(this.sortedStudies.length).then(chunk => {
      this.sortedStudies = this.sortedStudies.concat(chunk);
    });
  }

  protected fetchNextChunk(items: number): Promise<any> {
    const nextPage = (items / this.studiesPerPage) + 1;
    return this.getStudiesForPage(nextPage);
  }

  protected getStudiesForPage(nextPage): Promise<any> {
    return new Promise((resolve) => {
      this.studyService.getStudiesForOverview(nextPage).subscribe((studies) => {
          this.totalPages = studies.last_page;
          const transformedStudies = [];
          for (const study of studies.data) {
            const transformedStudy: Study = this.dashboardCamelCasePipe.keysToCamelCase(study);
            if (!transformedStudy.pivot || (transformedStudy.pivot && !transformedStudy.pivot.blocked)) {
              transformedStudy.status = setStudyStatus(transformedStudy.startsAt, transformedStudy.endsAt);
              transformedStudies.push(transformStudyFromApi(transformedStudy));
            }
          }
          resolve(transformedStudies);
        }
      );
    });
  }
}
